import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Check, Clock, Slash, Sparkles, Plus } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import type { Command } from '@/commands/types';
import { CATEGORY_ORDER, CATEGORY_LABELS } from '@/commands/categories';
import type { useRecentCommands } from '@/hooks/useRecentCommands';
import { parseSlash, acceptGhostCompletion } from '@/commands/slashParser';
import {
  AI_SUBCOMMANDS,
  getGhostSuffix,
  type SlashVerb,
  type SlashSubcommand,
} from '@/commands/slashCommands';
import { SlashInput } from './SlashInput';
import {
  SlashCommandList,
  getSlashItemCount,
  getActiveSlashItem,
} from './SlashCommandList';
import type { SlashAiHandlerOptions } from '@/commands/slashAiHandlers';
import { createAiExecutors } from '@/commands/slashAiHandlers';
import type { ScadFeature } from '@/lib/scad-feature-types';
import {
  PRIMITIVES,
  TRANSFORMS,
  BOOLEAN_OPS,
  ADVANCED_OPS,
  EXTRUDE_OPS,
  CLONE_OPS,
  LIGHTS,
  SCENE,
  CAMERAS,
  SCENE_GRIDS,
  SCENE_CAMERA,
  SVG_OVERLAY,
  INTERACTION_FEATURES,
  MOTION_FEATURES,
  MESH_CLONE,
  MATERIAL_DEF,
  SHADER_MATERIAL,
  JS_SVG,
  SNIPPETS,
  MODIFIER_OPS,
  RENDER_SETTINGS,
} from '@/lib/scad-constants';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commands: Command[];
  recents: string[];
  onRecordUsed: ReturnType<typeof useRecentCommands>['recordUsed'];
  /** Slash-command AI handlers — required for /ai sub-commands */
  slashAiHandlers?: SlashAiHandlerOptions;
  /** Editor insert callback for /add commands */
  onInsertCode?: (text: string) => void;
  /** Current SCAD code — used to determine if modify/explain/improve are available */
  scadCode?: string;
}

// ─── /add feature map ─────────────────────────────────────────────────────────

/**
 * All insertable ScadFeature objects keyed by a slug used as the sub-command name.
 * The name is derived from the Command id (e.g. "insert.cube" → "cube").
 */
interface AddEntry {
  name: string;
  label: string;
  description: string;
  feature: ScadFeature | null;
  /** Raw code for features without ScadFeature (snippet strings) */
  code?: string;
  /** Icon component */
  icon?: React.ComponentType<{ className?: string }>;
}

function buildAddEntries(): AddEntry[] {
  const entries: AddEntry[] = [];

  const add = (
    id: string,
    feature: ScadFeature,
    icon?: React.ComponentType<{ className?: string }>,
  ) => {
    entries.push({
      name: id,
      label: feature.label,
      description: feature.description ?? `Insert ${feature.label}`,
      feature,
      icon,
    });
  };

  // Primitives
  add('cube', PRIMITIVES.cube as ScadFeature);
  add('sphere', PRIMITIVES.sphere as ScadFeature);
  add('cylinder', PRIMITIVES.cylinder as ScadFeature);
  add('circle', PRIMITIVES.circle as ScadFeature);
  add('square', PRIMITIVES.square as ScadFeature);
  add('text', PRIMITIVES.text as ScadFeature);

  // Booleans
  add('union', BOOLEAN_OPS.union as ScadFeature);
  add('difference', BOOLEAN_OPS.difference as ScadFeature);
  add('intersection', BOOLEAN_OPS.intersection as ScadFeature);
  add('hull', ADVANCED_OPS.hull as ScadFeature);
  add('minkowski', ADVANCED_OPS.minkowski as ScadFeature);

  // Transforms
  add('translate', TRANSFORMS.translate as ScadFeature);
  add('rotate', TRANSFORMS.rotate as ScadFeature);
  add('scale', TRANSFORMS.scale as ScadFeature);
  add('resize', TRANSFORMS.resize as ScadFeature);
  add('mirror', TRANSFORMS.mirror as ScadFeature);
  add('color', TRANSFORMS.color as ScadFeature);
  add('material', TRANSFORMS.material as ScadFeature);

  // Extrusions & clones
  add('linear-extrude', EXTRUDE_OPS.linear_extrude as ScadFeature);
  add('rotate-extrude', EXTRUDE_OPS.rotate_extrude as ScadFeature);
  add('clone-1d', CLONE_OPS.clone_1d as ScadFeature);
  add('clone-2d', CLONE_OPS.clone_2d as ScadFeature);
  add('clone-3d', CLONE_OPS.clone_3d as ScadFeature);

  // Lights
  add('ambient-light', LIGHTS.ambient as ScadFeature);
  add('directional-light', LIGHTS.directional as ScadFeature);
  add('point-light', LIGHTS.point as ScadFeature);
  add('spot-light', LIGHTS.spot as ScadFeature);

  // Scene
  add('scene-material', SCENE.scene_material as ScadFeature);
  add('scene-lights', SCENE.scene_default_lights as ScadFeature);
  add('scene-shadows', SCENE.scene_shadows as ScadFeature);
  add('scene-bloom', SCENE.scene_bloom as ScadFeature);
  add('scene-ssao', SCENE.scene_ssao as ScadFeature);
  add('scene-background', SCENE.scene_background as ScadFeature);
  add('plane-grid', SCENE_GRIDS.plane_grid as ScadFeature);
  add('cube-wireframe', SCENE_GRIDS.cube_wireframe as ScadFeature);
  add('sphere-wireframe', SCENE_GRIDS.sphere_wireframe as ScadFeature);
  add('camera-pip', CAMERAS.pip as ScadFeature);
  add('camera-pip-ortho', CAMERAS.pip_ortho as ScadFeature);
  add('scene-camera', SCENE_CAMERA.scene_camera as ScadFeature);
  add('svg-overlay', SVG_OVERLAY.svg_overlay as ScadFeature);

  // Interaction & motion
  add('annotation', INTERACTION_FEATURES.annotation as ScadFeature);
  add('interactive', INTERACTION_FEATURES.interactive as ScadFeature);
  add('interactive-publish', INTERACTION_FEATURES.interactive_publish as ScadFeature);
  add('motion-translate', MOTION_FEATURES.motion_translate as ScadFeature);
  add('motion-orbit', MOTION_FEATURES.motion_orbit as ScadFeature);
  add('motion-spin', MOTION_FEATURES.motion_spin as ScadFeature);

  // Mesh clone
  add('clone-ref', MESH_CLONE.clone_ref as ScadFeature);
  add('clone-instance', MESH_CLONE.clone as ScadFeature);

  // Materials / shaders
  add('material-def', MATERIAL_DEF.material_def as ScadFeature);
  add('shader-material', SHADER_MATERIAL.shader_material as ScadFeature);
  add('js-svg', JS_SVG.js_svg as ScadFeature);

  // Snippets (raw code, no fields)
  entries.push({ name: 'module', label: 'Module', description: 'Insert a module definition', feature: null, code: SNIPPETS.module.code });
  entries.push({ name: 'function', label: 'Function', description: 'Insert a function definition', feature: null, code: SNIPPETS.function.code });
  entries.push({ name: 'for-loop', label: 'For Loop', description: 'Insert a for loop', feature: null, code: SNIPPETS.for_loop.code });
  entries.push({ name: 'if-else', label: 'If/Else', description: 'Insert an if/else block', feature: null, code: SNIPPETS.if_else.code });
  entries.push({ name: 'polygon', label: 'Polygon', description: 'Insert a polygon shape', feature: null, code: SNIPPETS.polygon.code });
  entries.push({ name: 'polyhedron', label: 'Polyhedron', description: 'Insert a polyhedron', feature: null, code: SNIPPETS.polyhedron.code });

  // Modifiers
  add('debug', MODIFIER_OPS.debug as ScadFeature);
  add('background', MODIFIER_OPS.background as ScadFeature);
  add('root', MODIFIER_OPS.root as ScadFeature);
  add('disable', MODIFIER_OPS.disable as ScadFeature);

  // Render settings
  add('render-fn', RENDER_SETTINGS.render_fn as ScadFeature);
  add('render-fa', RENDER_SETTINGS.render_fa as ScadFeature);
  add('render-fs', RENDER_SETTINGS.render_fs as ScadFeature);

  return entries;
}

const ADD_ENTRIES = buildAddEntries();

// ─── Build SlashVerb list ─────────────────────────────────────────────────────

function buildSlashVerbs(
  aiHandlers: SlashAiHandlerOptions | undefined,
  insertCode: (text: string) => void,
): SlashVerb[] {
  const aiExecutors = aiHandlers ? createAiExecutors(aiHandlers) : null;

  // /ai verb
  const aiVerb: SlashVerb = {
    verb: 'ai',
    label: 'AI Assistant',
    description: 'create, modify, explain, improve, fix',
    icon: Sparkles,
    subcommands: AI_SUBCOMMANDS.map((sub) => ({
      ...sub,
      execute: aiExecutors
        ? aiExecutors[sub.name as keyof typeof aiExecutors]
        : () => {},
      icon: Sparkles,
    })),
  };

  // /add verb — built from ADD_ENTRIES
  const addVerb: SlashVerb = {
    verb: 'add',
    label: 'Insert Code',
    description: 'Insert a shape, transform, or code snippet',
    icon: Plus,
    subcommands: ADD_ENTRIES.map((entry) => ({
      name: entry.name,
      label: entry.label,
      argLabel: entry.feature?.fields?.some((f) => f.type === 'number')
        ? '[size]'
        : undefined,
      description: entry.description,
      hasArg: !!entry.feature?.fields?.some((f) => f.type === 'number'),
      execute: (arg?: string) => {
        const numArg = arg ? parseFloat(arg.trim()) : NaN;
        const hasValidArg = !isNaN(numArg);

        if (entry.feature) {
          // If a numeric arg was given and the feature has numeric fields,
          // inject it and call generate() directly
          if (hasValidArg && entry.feature.fields && entry.feature.generate) {
            const firstNumericField = entry.feature.fields.find(
              (f) => f.type === 'number',
            );
            if (firstNumericField) {
              const values = entry.feature.fields.reduce(
                (acc, f) => ({ ...acc, [f.name]: f.default }),
                {} as Record<string, unknown>,
              );
              values[firstNumericField.name] = numArg;
              const code = entry.feature.generate(values);
              insertCode(code);
              return;
            }
          }
          // No arg (or no numeric fields) — insert the default code
          if (entry.feature.code) {
            insertCode(entry.feature.code);
          }
        } else if (entry.code) {
          insertCode(entry.code);
        }
      },
      icon: Plus,
    })),
  };

  return [aiVerb, addVerb];
}

// ─── Main component ────────────────────────────────────────────────────────────

export function CommandPalette({
  open,
  onOpenChange,
  commands,
  recents,
  onRecordUsed,
  slashAiHandlers,
  onInsertCode,
  scadCode,
}: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [slashInput, setSlashInput] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  // Build slash verbs whenever handlers/insert change
  const slashVerbs = useMemo(
    () => buildSlashVerbs(slashAiHandlers, onInsertCode ?? (() => {})),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slashAiHandlers, onInsertCode],
  );

  const isSlashMode = slashInput.startsWith('/');
  const parsed = isSlashMode ? parseSlash(slashInput) : parseSlash('');

  // Ghost text
  const ghostFull = isSlashMode ? getGhostSuffix(slashInput, slashVerbs) : '';
  const ghostText = slashInput + ghostFull;

  // Reset active index when input changes
  useEffect(() => {
    setActiveIndex(0);
  }, [slashInput]);

  // Reset slash input when palette closes
  useEffect(() => {
    if (!open) {
      setSlashInput('');
    }
  }, [open]);

  // Focus the slash input when palette opens in slash mode
  useEffect(() => {
    if (open && isSlashMode) {
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open, isSlashMode]);

  // ── Normal command palette handlers ─────────────────────────────────────────

  const handleSelect = useCallback(
    (cmd: Command) => {
      onRecordUsed(cmd.id);
      onOpenChange(false);
      setTimeout(() => {
        cmd.execute();
      }, 50);
    },
    [onOpenChange, onRecordUsed],
  );

  // ── Slash mode: Tab completion ───────────────────────────────────────────────

  const handleTabComplete = useCallback(() => {
    if (!isSlashMode) return;

    // Find best verb match
    const withoutSlash = slashInput.slice(1);
    const parts = withoutSlash.split(' ');
    const verbPartial = parts[0].toLowerCase();
    const bestVerb = slashVerbs.find((v) => v.verb.startsWith(verbPartial));

    // Find best sub match
    let bestSub: string | null = null;
    if (bestVerb && parts.length >= 2) {
      const subPartial = parts[1].toLowerCase();
      const matched = bestVerb.subcommands.find((s) =>
        s.name.startsWith(subPartial),
      );
      bestSub = matched?.name ?? null;
    } else if (bestVerb && parts.length === 1) {
      bestSub = bestVerb.subcommands[0]?.name ?? null;
    }

    const completed = acceptGhostCompletion(slashInput, bestVerb?.verb ?? null, bestSub);
    if (completed !== slashInput) {
      setSlashInput(completed);
    }
  }, [isSlashMode, slashInput, slashVerbs]);

  // ── Slash mode: Enter / select ───────────────────────────────────────────────

  const handleSlashEnter = useCallback(() => {
    if (!isSlashMode) return;

    const item = getActiveSlashItem(parsed, slashVerbs, activeIndex);
    if (!item) return;

    const { verb, sub } = item;

    // Phase: verb — advance to sub phase
    if (!sub) {
      setSlashInput(`/${verb.verb} `);
      return;
    }

    // Phase: sub or arg — execute
    const hasArgNeeded = sub.hasArg && sub.argRequired;

    if (hasArgNeeded && (!parsed.arg || !parsed.arg.trim())) {
      // Need an arg but none given — advance to arg phase with space appended
      const withSub = `/${verb.verb} ${sub.name} `;
      if (slashInput !== withSub) {
        setSlashInput(withSub);
      }
      return;
    }

    // Execute and close
    onOpenChange(false);
    setTimeout(() => {
      sub.execute(parsed.arg ?? undefined);
    }, 50);
  }, [isSlashMode, parsed, slashVerbs, activeIndex, slashInput, onOpenChange]);

  // ── Slash mode: item click ───────────────────────────────────────────────────

  const handleSlashSelect = useCallback(
    (verb: SlashVerb, sub?: SlashSubcommand) => {
      if (!sub) {
        // Verb selected — advance to sub phase
        setSlashInput(`/${verb.verb} `);
        inputRef.current?.focus();
        return;
      }

      // Sub selected in 'sub' phase (no arg typed) — advance to arg phase
      if (parsed.phase === 'sub' || parsed.phase === 'verb') {
        const next = `/${verb.verb} ${sub.name}${sub.hasArg ? ' ' : ''}`;
        setSlashInput(next);
        inputRef.current?.focus();
        if (!sub.hasArg) {
          // No arg needed — execute immediately
          onOpenChange(false);
          setTimeout(() => sub.execute(), 50);
        }
        return;
      }

      // Arg phase — execute
      onOpenChange(false);
      setTimeout(() => sub.execute(parsed.arg ?? undefined), 50);
    },
    [parsed, onOpenChange],
  );

  // ── Arrow navigation ─────────────────────────────────────────────────────────

  const handleArrowUp = useCallback(() => {
    if (!isSlashMode) return;
    const count = getSlashItemCount(parsed, slashVerbs);
    setActiveIndex((prev) => (prev <= 0 ? count - 1 : prev - 1));
  }, [isSlashMode, parsed, slashVerbs]);

  const handleArrowDown = useCallback(() => {
    if (!isSlashMode) return;
    const count = getSlashItemCount(parsed, slashVerbs);
    setActiveIndex((prev) => (prev >= count - 1 ? 0 : prev + 1));
  }, [isSlashMode, parsed, slashVerbs]);

  // ── Input change — detect "/" to switch modes ────────────────────────────────

  const handleInputChange = useCallback((value: string) => {
    setSlashInput(value);
  }, []);

  // ─── Render ──────────────────────────────────────────────────────────────────

  // Map commands by id for quick lookup
  const commandMap = new Map(commands.map((c) => [c.id, c]));
  const recentCommands = recents
    .map((id) => commandMap.get(id))
    .filter((c): c is Command => !!c);
  const byCategory = new Map<string, Command[]>();
  for (const cmd of commands) {
    const list = byCategory.get(cmd.category) ?? [];
    list.push(cmd);
    byCategory.set(cmd.category, list);
  }

  // ── Slash mode ────────────────────────────────────────────────────────────────
  if (isSlashMode) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="overflow-hidden p-0 sm:max-w-[600px]">
          <VisuallyHidden>
            <DialogTitle>Slash Commands</DialogTitle>
          </VisuallyHidden>
          {/* Input area */}
          <div className="border-b border-border">
            <div className="flex items-center gap-2 px-3 py-1">
              <Slash className="h-4 w-4 shrink-0 text-muted-foreground" />
              <SlashInput
                ref={inputRef}
                value={slashInput}
                onChange={handleInputChange}
                onTabComplete={handleTabComplete}
                onEnter={handleSlashEnter}
                onEscape={() => onOpenChange(false)}
                onArrowUp={handleArrowUp}
                onArrowDown={handleArrowDown}
                ghostText={ghostText}
                placeholder="/ai create ... or /add cube ..."
                autoFocus
                className="flex-1"
              />
              <kbd className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                Tab
              </kbd>
              <span className="text-[10px] text-muted-foreground">to complete</span>
            </div>
            {/* Phase breadcrumb */}
            {parsed.verb && (
              <div className="flex items-center gap-1 border-t border-border/50 bg-muted/30 px-4 py-1.5">
                <span className="font-mono text-[11px] text-muted-foreground">
                  /{parsed.verb}
                </span>
                {parsed.sub && (
                  <>
                    <span className="text-[11px] text-muted-foreground/50">›</span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {parsed.sub}
                    </span>
                  </>
                )}
                {parsed.arg !== null && parsed.arg !== undefined && (
                  <>
                    <span className="text-[11px] text-muted-foreground/50">›</span>
                    <span className="max-w-[300px] truncate font-mono text-[11px] text-primary/70">
                      {parsed.arg || <em className="not-italic text-muted-foreground/50">type your prompt…</em>}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Results */}
          <div className="max-h-[420px] overflow-y-auto">
            <SlashCommandList
              parsed={parsed}
              verbs={slashVerbs}
              activeIndex={activeIndex}
              onActiveIndexChange={setActiveIndex}
              onSelect={handleSlashSelect}
            />
          </div>

          {/* Footer hint */}
          <div className="border-t border-border bg-muted/30 px-4 py-2">
            <p className="text-[11px] text-muted-foreground">
              <kbd className="mr-1 rounded border border-border bg-background px-1 py-0.5 font-mono text-[10px]">↑↓</kbd>
              navigate
              <span className="mx-2">·</span>
              <kbd className="mr-1 rounded border border-border bg-background px-1 py-0.5 font-mono text-[10px]">↵</kbd>
              select
              <span className="mx-2">·</span>
              <kbd className="mr-1 rounded border border-border bg-background px-1 py-0.5 font-mono text-[10px]">Tab</kbd>
              complete
              <span className="mx-2">·</span>
              <kbd className="mr-1 rounded border border-border bg-background px-1 py-0.5 font-mono text-[10px]">Esc</kbd>
              close
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Normal cmdk mode ──────────────────────────────────────────────────────────
  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder='Search commands… (type "/" for slash commands)'
        value={slashInput}
        onValueChange={handleInputChange}
      />
      <CommandList className="max-h-[480px]">
        <CommandEmpty>No commands found.</CommandEmpty>

        {/* Recent commands */}
        {recentCommands.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recentCommands.map((cmd) => (
                <PaletteItem
                  key={`recent-${cmd.id}`}
                  cmd={cmd}
                  showRecent
                  onSelect={handleSelect}
                />
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* All categories in defined order */}
        {CATEGORY_ORDER.map((category) => {
          const cmds = byCategory.get(category);
          if (!cmds || cmds.length === 0) return null;
          return (
            <CommandGroup key={category} heading={CATEGORY_LABELS[category]}>
              {cmds.map((cmd) => (
                <PaletteItem key={cmd.id} cmd={cmd} onSelect={handleSelect} />
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
}

// ─── PaletteItem ──────────────────────────────────────────────────────────────

interface PaletteItemProps {
  cmd: Command;
  showRecent?: boolean;
  onSelect: (cmd: Command) => void;
}

function PaletteItem({ cmd, showRecent, onSelect }: PaletteItemProps) {
  const Icon = cmd.icon;
  const isToggle = cmd.getState !== undefined;
  const isOn = isToggle ? cmd.getState!() : undefined;

  return (
    <CommandItem
      value={`${cmd.id} ${cmd.label} ${cmd.keywords?.join(' ') ?? ''}`}
      onSelect={() => onSelect(cmd)}
      className="flex items-center gap-2"
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
        {showRecent ? (
          <Clock className="h-3.5 w-3.5" />
        ) : Icon ? (
          <Icon className="h-3.5 w-3.5" />
        ) : null}
      </span>

      <span className="flex-1 truncate">{cmd.label}</span>

      {cmd.shortcut && (
        <kbd className="ml-2 shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
          {cmd.shortcut}
        </kbd>
      )}

      {isToggle && (
        <span
          className={`ml-1 flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium tabular-nums ${
            isOn
              ? 'bg-primary/10 text-primary'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          {isOn ? (
            <>
              <Check className="h-2.5 w-2.5" />
              ON
            </>
          ) : (
            'OFF'
          )}
        </span>
      )}
    </CommandItem>
  );
}
